import { personImage } from "../utils/personImage";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CalendarDays,
  ChartNoAxesCombined,
  CircleUserRound,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Store,
  TrendingDown,
  TrendingUp,
  UserCheck,
  UserPlus,
  UsersRound,
  MessageSquareText,
  Save,
  Tag,
  X,
} from "lucide-react";
import VisitTable from "../components/VisitTable";

const CUSTOMER_COLORS = ["#3b82f6", "#ec4899"];

function getTodayThai() {
  return new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Bangkok" });
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("th-TH");
}

function formatThaiDate(date) {
  if (!date) return "";
  return new Intl.DateTimeFormat("th-TH", { day: "numeric", month: "long", year: "numeric" }).format(new Date(`${date}T00:00:00`));
}

function CustomerTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const percent = total ? Math.round((Number(item.value) / total) * 100) : 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-xl backdrop-blur">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-600"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.payload.fill }} />{item.name}</div>
      <p className="mt-1 text-xl font-semibold text-slate-900">{formatNumber(item.value)} <span className="text-xs font-normal text-slate-400">คน · {percent}%</span></p>
    </div>
  );
}

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="min-w-[160px] rounded-xl border border-slate-700 bg-slate-900/95 px-4 py-3 text-white shadow-xl backdrop-blur">
      <p className="mb-2 text-xs font-medium text-slate-300">เดือน {label}</p>
      {payload.map((item) => (
        <div key={item.dataKey} className="flex items-center justify-between gap-5 py-0.5 text-xs">
          <span className="flex items-center gap-2 text-slate-300"><i className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />{item.dataKey === "new" ? "ลูกค้าใหม่" : "ลูกค้ากลับมา"}</span>
          <strong className="text-sm text-white">{formatNumber(item.value)}</strong>
        </div>
      ))}
    </div>
  );
}

function MetricCard({ title, value, suffix, icon: Icon, tone = "teal", growth, detail }) {
  const tones = {
    teal: "bg-teal-50 text-teal-700 ring-teal-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    pink: "bg-pink-50 text-pink-700 ring-pink-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    cyan: "bg-cyan-50 text-cyan-700 ring-cyan-100",
  };
  const growthNumber = Number(growth || 0);

  return (
    <div className="group rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <div className="mt-2 flex items-end gap-1.5"><span className="text-3xl font-semibold tracking-tight text-slate-900">{value}</span>{suffix && <span className="pb-1 text-xs text-slate-400">{suffix}</span>}</div>
        </div>
        <div className={`rounded-xl p-2.5 ring-1 ${tones[tone] || tones.teal}`}><Icon size={20} strokeWidth={1.8} /></div>
      </div>
      {growth !== undefined ? (
        <div className={`mt-3 flex items-center gap-1.5 text-xs font-medium ${growthNumber > 0 ? "text-emerald-600" : growthNumber < 0 ? "text-red-500" : "text-slate-400"}`}>
          {growthNumber > 0 ? <TrendingUp size={14} /> : growthNumber < 0 ? <TrendingDown size={14} /> : <span className="h-px w-3 bg-slate-300" />}
          {growthNumber > 0 ? "+" : ""}{growthNumber}% <span className="font-normal text-slate-400">เทียบวันก่อน</span>
        </div>
      ) : detail ? <p className="mt-3 truncate text-xs text-slate-400">{detail}</p> : null}
    </div>
  );
}

function CustomerMixChart({ total, newCustomer, returningCustomer }) {
  const chartData = [
    { name: "ลูกค้าใหม่", value: Number(newCustomer || 0), fill: CUSTOMER_COLORS[0] },
    { name: "ลูกค้ากลับมา", value: Number(returningCustomer || 0), fill: CUSTOMER_COLORS[1] },
  ];

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
      <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-gradient-to-br from-teal-100/70 to-cyan-50/60 blur-2xl" />
      <div className="relative flex items-start justify-between">
        <div><h2 className="font-semibold text-slate-900">องค์ประกอบลูกค้า</h2><p className="mt-1 text-xs text-slate-500">สัดส่วนลูกค้าใหม่และลูกค้าที่กลับมา</p></div>
        <div className="rounded-xl bg-slate-50 p-2.5 text-slate-500 ring-1 ring-slate-100"><UsersRound size={19} /></div>
      </div>
      <div className="relative mt-2 grid min-h-[285px] grid-cols-1 items-center sm:grid-cols-[1.1fr_0.9fr]">
        <div className="relative h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart><Pie data={chartData} dataKey="value" nameKey="name" innerRadius={70} outerRadius={96} paddingAngle={4} cornerRadius={9} stroke="none">{chartData.map((item) => <Cell key={item.name} fill={item.fill} />)}</Pie><Tooltip content={<CustomerTooltip total={Number(total || 0)} />} /></PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"><span className="text-xs text-slate-400">ลูกค้าทั้งหมด</span><strong className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{formatNumber(total)}</strong><span className="text-xs text-slate-400">คน</span></div>
        </div>
        <div className="space-y-3 pb-3 sm:pb-0 sm:pr-2">
          {chartData.map((item) => {
            const percent = total ? Math.round((item.value / total) * 100) : 0;
            return (
              <div key={item.name} className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
                <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-sm font-medium text-slate-600"><i className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.fill }} />{item.name}</span><strong className="text-sm text-slate-900">{percent}%</strong></div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: item.fill }} /></div>
                <p className="mt-1.5 text-right text-xs text-slate-400">{formatNumber(item.value)} คน</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthlyTrendChart({ data }) {
  const chartData = Array.isArray(data) ? data : [];
  const totals = chartData.reduce((acc, item) => ({ new: acc.new + Number(item.new || 0), returning: acc.returning + Number(item.returning || 0) }), { new: 0, returning: 0 });

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
      <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-teal-100/60 blur-3xl" />
      <div className="relative flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div><h2 className="font-semibold text-slate-900">แนวโน้มลูกค้ารายเดือน</h2><p className="mt-1 text-xs text-slate-500">เปรียบเทียบการเติบโตของฐานลูกค้าในแต่ละเดือน</p></div>
        <div className="flex gap-3 text-xs"><span className="flex items-center gap-1.5 text-slate-500"><i className="h-2.5 w-2.5 rounded-full bg-blue-500" /> ใหม่ {formatNumber(totals.new)}</span><span className="flex items-center gap-1.5 text-slate-500"><i className="h-2.5 w-2.5 rounded-full bg-pink-500" /> กลับมา {formatNumber(totals.returning)}</span></div>
      </div>
      <div className="relative mt-5 h-[285px] min-h-[285px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
            <defs><linearGradient id="newCustomerArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3b82f6" stopOpacity={0.22} /><stop offset="100%" stopColor="#3b82f6" stopOpacity={0.01} /></linearGradient></defs>
            <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="4 4" />
            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 11 }} dy={9} />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
            <Tooltip content={<TrendTooltip />} cursor={{ stroke: "#cbd5e1", strokeDasharray: "4 4" }} />
            <Area type="monotone" dataKey="new" fill="url(#newCustomerArea)" stroke="#3b82f6" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: "#3b82f6", stroke: "white", strokeWidth: 3 }} />
            <Line type="monotone" dataKey="returning" stroke="#ec4899" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: "#ec4899", stroke: "white", strokeWidth: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function CustomerInsight() {
  const [data, setData] = useState(null);
  const [stores, setStores] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState({ by: "time", direction: "desc" });
  const [applied, setApplied] = useState({ date: getTodayThai(), store: "all" });
  const request = useRef(null);
  const [date, setDate] = useState(getTodayThai());
  const [storeId, setStoreId] = useState("all");
  const [activeTab, setActiveTab] = useState("new");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [metaCustomer, setMetaCustomer] = useState(null);
  const [metaForm, setMetaForm] = useState({ tags: [], note: "" });
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaError, setMetaError] = useState("");

  const fetchData = async ({ silent = false, selectedDate = date, selectedStore = storeId, selectedTab = activeTab, selectedPage = 1, selectedSize = pageSize, selectedSort = sort } = {}) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    silent ? setRefreshing(true) : setLoading(true);
    setError("");
    const token = localStorage.getItem("token");
    const params = new URLSearchParams({ date: selectedDate, tab: selectedTab, page: selectedPage, page_size: selectedSize, sort_by: selectedSort.by, sort_dir: selectedSort.direction });
    if (selectedStore !== "all") params.append("store_id", selectedStore);

    try {
      const response = await fetch(`/api/customer-insight/overview?${params}`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
      if (!response.ok) throw new Error("ไม่สามารถโหลดข้อมูลลูกค้าเชิงลึกได้");
      const summaryJson = await response.json();
      if (controller.signal.aborted) return;
      setData(summaryJson.data || {});
      setStores(summaryJson.stores || []);
      setCustomers(summaryJson.customers?.rows || []);
      setActiveTab(summaryJson.customers?.tab || selectedTab);
      setPage(summaryJson.customers?.page || 1);
      setPageSize(summaryJson.customers?.page_size || selectedSize);
      setSort(selectedSort);
      setApplied({ date: selectedDate, store: selectedStore });
    } catch (fetchError) {
      if (fetchError.name !== "AbortError") setError(fetchError.message || "เกิดข้อผิดพลาด กรุณาลองอีกครั้ง");
    } finally {
      if (request.current === controller) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      window.location.href = "/login";
      return;
    }
    fetchData();
    return () => request.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedStoreName = stores.find((store) => String(store.store_id) === String(applied.store))?.device_name || "ทุกสาขา";
  const activeCustomers = customers;
  const activeTotal = activeTab === "new" ? Number(data?.new_customer || 0) : Number(data?.returning_customer || 0);
  const loadTable = (options) => fetchData({ silent: true, selectedDate: applied.date, selectedStore: applied.store, ...options });
  const activeTitle = activeTab === "new" ? "ลูกค้าใหม่" : "ลูกค้าที่กลับมา";
  const returnRate = Number(data?.returning_rate || 0);
  const insightMessage = useMemo(() => {
    if (!data?.total) return "ยังไม่มีข้อมูลลูกค้าในวันที่เลือก";
    if (returnRate >= 50) return "ลูกค้ามากกว่าครึ่งเป็นลูกค้าที่กลับมา สะท้อนการรักษาฐานลูกค้าที่ดี";
    if (Number(data.new_customer) > Number(data.returning_customer)) return "วันนี้มีลูกค้าใหม่มากกว่าลูกค้าที่กลับมา เหมาะกับการติดตามผลการหาลูกค้าใหม่";
    return "สัดส่วนลูกค้าใหม่และลูกค้าที่กลับมาอยู่ในระดับใกล้เคียงกัน";
  }, [data, returnRate]);

  const openMetaEditor = (customer) => {
    setMetaCustomer(customer);
    setMetaForm({
      tags: Array.isArray(customer.customer_tags) ? customer.customer_tags : [],
      note: customer.customer_note || "",
    });
    setMetaError("");
  };

  const closeMetaEditor = () => {
    if (metaSaving) return;
    setMetaCustomer(null);
    setMetaError("");
  };

  const saveCustomerMeta = async () => {
    if (!metaCustomer?.ulucu_user_id) return;
    setMetaSaving(true);
    setMetaError("");
    try {
      const response = await fetch(`/api/customer-insight/customer/${metaCustomer.ulucu_user_id}/meta`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(metaForm),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || "บันทึก Tag และ Note ไม่สำเร็จ");

      const applyMeta = (customers) => customers.map((customer) =>
        customer.ulucu_user_id === metaCustomer.ulucu_user_id
          ? {
              ...customer,
              customer_tags: json.data?.tags || [],
              customer_note: json.data?.note || "",
              customer_meta_updated_at: json.data?.updatedAt || new Date().toISOString(),
            }
          : customer
      );
      setCustomers(applyMeta);
      setMetaSaving(false);
      closeMetaEditor();
    } catch (saveError) {
      setMetaError(saveError.message);
    } finally {
      setMetaSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 p-5 md:p-8"><div className="mx-auto max-w-[1600px] animate-pulse space-y-6"><div className="h-20 rounded-2xl bg-slate-200" /><div className="h-24 rounded-2xl bg-white" /><div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[1, 2, 3, 4].map((item) => <div key={item} className="h-32 rounded-2xl bg-white" />)}</div><div className="grid gap-6 xl:grid-cols-2"><div className="h-96 rounded-2xl bg-white" /><div className="h-96 rounded-2xl bg-white" /></div></div></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div><div className="mb-2 flex items-center gap-2 text-sm font-medium text-cyan-700"><span className="h-2 w-2 rounded-full bg-cyan-500" /> Customer intelligence</div><h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">ข้อมูลลูกค้าเชิงลึก</h1><p className="mt-1 text-sm text-slate-500">เข้าใจลูกค้าใหม่ การกลับมาใช้บริการ และแนวโน้มการเติบโตของฐานลูกค้า</p></div>
          <button onClick={() => fetchData({ silent: true })} disabled={refreshing} className="flex h-11 items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-sm transition hover:border-[#00b3b4]/30 hover:bg-teal-50 hover:text-[#008f90] disabled:opacity-60 lg:self-auto"><RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />{refreshing ? "กำลังอัปเดต" : "อัปเดตข้อมูล"}</button>
        </header>

        <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm md:p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto]">
            <label className="space-y-1.5"><span className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><CalendarDays size={13} /> วันที่</span><input type="date" value={date} max={getTodayThai()} onChange={(event) => setDate(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10" /></label>
            <label className="space-y-1.5"><span className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><Store size={13} /> สาขา</span><select value={storeId} onChange={(event) => setStoreId(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10"><option value="all">ทุกสาขา</option>{stores.map((store) => <option key={store.store_id} value={store.store_id}>{store.device_name}</option>)}</select></label>
            <div className="flex items-end gap-2"><button onClick={() => fetchData()} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[#00b3b4] px-5 text-sm font-semibold text-white shadow-sm shadow-teal-200 transition hover:bg-[#008f90] focus:outline-none focus:ring-4 focus:ring-teal-500/20"><ChartNoAxesCombined size={16} /> แสดงผล</button><button onClick={() => { setDate(getTodayThai()); setStoreId("all"); fetchData({ selectedDate: getTodayThai(), selectedStore: "all" }); }} title="ล้างตัวกรอง" className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-[#00b3b4]/30 hover:bg-teal-50 hover:text-[#008f90]"><RotateCcw size={16} /></button></div>
          </div>
        </section>

        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <div className="flex items-start gap-3 rounded-2xl border border-cyan-100 bg-gradient-to-r from-cyan-50 to-white px-4 py-3.5"><div className="rounded-lg bg-white p-2 text-cyan-600 shadow-sm ring-1 ring-cyan-100"><Sparkles size={17} /></div><div><p className="text-sm font-medium text-cyan-900">Insight ประจำวันที่ {formatThaiDate(applied.date)} · {selectedStoreName}</p><p className="mt-0.5 text-xs leading-relaxed text-cyan-700/80">{insightMessage}</p></div></div>

        <section className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <MetricCard title="ลูกค้าทั้งหมด" value={formatNumber(data?.total)} suffix="คน" icon={UsersRound} detail="จำนวนลูกค้าไม่ซ้ำในวันนี้" />
          <MetricCard title="ลูกค้าใหม่" value={formatNumber(data?.new_customer)} suffix="คน" icon={UserPlus} tone="blue" growth={data?.new_growth} />
          <MetricCard title="ลูกค้าที่กลับมา" value={formatNumber(data?.returning_customer)} suffix="คน" icon={UserCheck} tone="pink" growth={data?.returning_growth} />
          <MetricCard title="อัตราการกลับมา" value={`${returnRate.toLocaleString("th-TH")}%`} icon={RefreshCw} tone="cyan" detail={`${formatNumber(data?.returning_customer)} จาก ${formatNumber(data?.total)} คน`} />
        </section>

        <section className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <CustomerMixChart total={Number(data?.total || 0)} newCustomer={data?.new_customer} returningCustomer={data?.returning_customer} />
          <MonthlyTrendChart data={data?.trend} />
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm"><div className="rounded-xl bg-amber-50 p-3 text-amber-700 ring-1 ring-amber-100"><CircleUserRound size={22} /></div><div><p className="text-sm text-slate-500">ลูกค้าประจำมากกว่า 5 ครั้ง</p><p className="mt-1 text-2xl font-semibold text-slate-900">{formatNumber(data?.returning_count)} <span className="text-xs font-normal text-slate-400">คน</span></p></div></div>
          <div className="flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm"><div className="rounded-xl bg-teal-50 p-3 text-teal-700 ring-1 ring-teal-100"><ChartNoAxesCombined size={22} /></div><div><p className="text-sm text-slate-500">ฐานลูกค้าที่ติดตามในแนวโน้ม</p><p className="mt-1 text-2xl font-semibold text-slate-900">{formatNumber((data?.trend || []).reduce((sum, item) => sum + Number(item.new || 0) + Number(item.returning || 0), 0))} <span className="text-xs font-normal text-slate-400">รายเดือนรวม</span></p></div></div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div><h2 className="font-semibold text-slate-900">รายชื่อลูกค้า</h2><p className="mt-0.5 text-xs text-slate-500">รายละเอียดลูกค้าตามวันที่และสาขาที่เลือก</p></div>
            <div className="flex rounded-xl bg-slate-100 p-1">
              <button onClick={() => loadTable({ selectedTab: "new" })} disabled={refreshing} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition ${activeTab === "new" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}><UserPlus size={14} /> ลูกค้าใหม่ <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700">{formatNumber(data?.new_customer)}</span></button>
              <button onClick={() => loadTable({ selectedTab: "returning" })} disabled={refreshing} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition ${activeTab === "returning" ? "bg-white text-pink-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}><UserCheck size={14} /> กลับมา <span className="rounded-full bg-pink-50 px-1.5 py-0.5 text-[10px] text-pink-700">{formatNumber(data?.returning_customer)}</span></button>
            </div>
          </div>
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-5 py-3"><span className="text-xs font-medium text-slate-500">กำลังแสดง: {activeTitle}</span><span className="text-xs text-slate-400">{formatNumber(activeTotal)} คน</span></div>
          <VisitTable key={`${applied.date}:${applied.store}:${activeTab}:${pageSize}`} data={activeCustomers} showCustomerMeta onEditCustomer={openMetaEditor} serverPagination={{ total: activeTotal, page, pageSize, loading: refreshing, sortBy: sort.by, sortDirection: sort.direction, onSort: (column, direction) => loadTable({ selectedSort: { by: column.sortField, direction } }), onPage: (next) => loadTable({ selectedPage: next }), onPageSize: (size) => loadTable({ selectedSize: size }) }} />
        </section>
      </div>

      {metaCustomer && (
        <CustomerMetaModal
          customer={metaCustomer}
          form={metaForm}
          setForm={setMetaForm}
          saving={metaSaving}
          error={metaError}
          close={closeMetaEditor}
          save={saveCustomerMeta}
        />
      )}
    </div>
  );
}

const suggestedTags = ["ลูกค้าประจำ", "สนใจสินค้า", "ติดตาม", "โปรโมชั่น", "VIP Potential", "ร้องเรียน"];

function CustomerMetaModal({ customer, form, setForm, saving, error, close, save }) {
  const [tagInput, setTagInput] = useState("");

  const addTag = (rawTag) => {
    const tag = rawTag.trim();
    if (!tag || form.tags.includes(tag) || form.tags.length >= 10) return;
    setForm({ ...form, tags: [...form.tags, tag] });
    setTagInput("");
  };

  const removeTag = (tag) => setForm({ ...form, tags: form.tags.filter((item) => item !== tag) });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm" onMouseDown={(event) => event.target === event.currentTarget && close()}>
      <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div className="flex items-center gap-3">
            <img src={personImage(customer.arrive_image_url, customer)} alt="รูปลูกค้า" className="h-12 w-12 rounded-xl border border-slate-200 object-cover" />
            <div><h2 className="text-lg font-semibold text-slate-900">Tag และ Note ลูกค้า</h2><p className="mt-0.5 font-mono text-xs text-slate-400">#{String(customer.ulucu_user_id).slice(-8)}</p></div>
          </div>
          <button onClick={close} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="space-y-6 p-6">
          <section>
            <div className="mb-2 flex items-center justify-between"><label className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Tag size={16} className="text-cyan-600" />Customer Tags</label><span className="text-xs text-slate-400">{form.tags.length}/10</span></div>
            <div className="flex min-h-[48px] flex-wrap items-center gap-2 rounded-xl border border-slate-200 p-2 focus-within:border-cyan-500 focus-within:ring-4 focus-within:ring-cyan-500/10">
              {form.tags.map((tag) => <span key={tag} className="inline-flex items-center gap-1.5 rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-medium text-cyan-700 ring-1 ring-cyan-100">{tag}<button onClick={() => removeTag(tag)} className="text-cyan-400 hover:text-cyan-800"><X size={12} /></button></span>)}
              <input value={tagInput} onChange={(event) => setTagInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === ",") { event.preventDefault(); addTag(tagInput); } }} onBlur={() => addTag(tagInput)} disabled={form.tags.length >= 10} placeholder={form.tags.length ? "เพิ่ม Tag..." : "พิมพ์ Tag แล้วกด Enter"} className="h-8 min-w-[150px] flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-slate-300" />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">{suggestedTags.filter((tag) => !form.tags.includes(tag)).map((tag) => <button key={tag} onClick={() => addTag(tag)} className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-500 transition hover:border-cyan-200 hover:bg-cyan-50 hover:text-cyan-700">+ {tag}</button>)}</div>
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between"><label className="flex items-center gap-2 text-sm font-semibold text-slate-800"><MessageSquareText size={16} className="text-[#00b3b4]" />Customer Note</label><span className="text-xs text-slate-400">{form.note.length}/2000</span></div>
            <textarea value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value.slice(0, 2000) })} rows={5} placeholder="บันทึกความสนใจ สิ่งที่ต้องติดตาม หรือรายละเอียดสำคัญของลูกค้า..." className="w-full resize-none rounded-xl border border-slate-200 p-3 text-sm leading-relaxed text-slate-700 outline-none transition focus:border-[#00b3b4] focus:ring-4 focus:ring-teal-500/10" />
          </section>

          {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4"><button onClick={close} disabled={saving} className="h-10 rounded-xl px-4 text-sm font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button><button onClick={save} disabled={saving} className="flex h-10 items-center gap-2 rounded-xl bg-[#00b3b4] px-5 text-sm font-medium text-white transition hover:bg-[#008f90] focus:outline-none focus:ring-4 focus:ring-teal-500/20 disabled:opacity-50">{saving ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}{saving ? "กำลังบันทึก" : "บันทึก Tag และ Note"}</button></div>
      </div>
    </div>
  );
}
